import React from 'react';
import { Box, Typography, Grid, Paper, Divider, List, ListItem, ListItemText } from '@mui/material';

const SectionPaper = ({ children, title }) => (
    <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2, border: '1px solid #f0f0f0' }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '0.8rem', sm: '1rem' } }}>
            {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
    </Paper>
);

const ViewAllDetailsModal = ({ open, handleClose, userDetails }) => {
    if (!userDetails) return null;

    const data = userDetails;
    const primaryAddress = data.addresses && data.addresses.length > 0
        ? data.addresses[0]
        : null;

    // 3. Format the address string
    const formattedAddress = primaryAddress
        ? `${primaryAddress.address_line}${primaryAddress.address_line2 ? `, ${primaryAddress.address_line2}` : ''}, ${primaryAddress.city}, ${primaryAddress.state} - ${primaryAddress.pincode}`
        : 'N/A';

    const DetailItem = ({ label, value }) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {value || '—'}
            </Typography>
        </Grid>
    );

    // Format dates for display
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString();
    };

    return (
        <Box>
            {/* --- 1. Personal & Contact Details --- */}
            <SectionPaper title="1. Personal & Contact Details">
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Full Name" value={`${data.first_name} ${data.last_name}`} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Date of Birth" value={formatDate(data.dob)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Gender" value={data.gender} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Marital Status" value={data.marital_status} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Blood Group" value={data.blood_group} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Phone Number" value={data.phone_number} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Personal Email" value={data.personal_email} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                        <DetailItem label="Address" value={formattedAddress} />
                    </Grid>
                </Grid>
            </SectionPaper>

            {/* --- 2. Employment Details --- */}
            <SectionPaper title="2. Employment Details">
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Employee ID" value={data.emp_id} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Designation" value={data.designation} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Department" value={data.department} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Team" value={data.team} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Joining Date" value={formatDate(data.doj)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Location" value={data.location_name} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Organization" value={data.organization_name} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Role" value={data.role_name} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem
                            label="Reporting Manager"
                            value={data.report_manager_first_name ? `${data.report_manager_first_name} ${data.report_manager_last_name}` : 'No Manager Assigned'}
                        />
                    </Grid>
                </Grid>
            </SectionPaper>

            {/* --- 3. Compensation & Pay --- */}
            <SectionPaper title="3. Compensation & Pay">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Salary Type" value={data.payroll?.salaryType} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem
                            label="Gross Salary"
                            value={`${data.payroll?.currency || ''} ${data.payroll?.grossSalary ? data.payroll.grossSalary.toLocaleString() : 'N/A'}`}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Pay Frequency" value={data.payroll?.payFrequency} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Variable Pay Applicable" value={data.payroll?.isVariablePayApplicable ? 'Yes' : 'No'} />
                    </Grid>
                    {data.payroll?.isVariablePayApplicable && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <DetailItem label="Variable Pay Percentage" value={`${data.payroll.variablePayPercentage}%`} />
                        </Grid>
                    )}
                </Grid>
            </SectionPaper>

            {/* --- 4. Banking & Statutory --- */}
            <SectionPaper title="4. Banking & Statutory">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Bank Name" value={data.bankingStatutory?.bankName} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Account No." value={data.bankingStatutory?.accountNumber} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="IFSC Code" value={data.bankingStatutory?.ifscCode} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="EPF Number" value={data.bankingStatutory?.epfNumber} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="UAN" value={data.bankingStatutory?.uan} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="PAN" value={data.bankingStatutory?.pan} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Aadhar" value={data.bankingStatutory?.aadhar} />
                    </Grid>
                </Grid>
            </SectionPaper>

            {/* --- 5. Assign Policies --- */}
            <SectionPaper title="5. Assigned Policies">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Leave Policy" value={data.policies?.leavePolicy?.toUpperCase()} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Shift Type" value={data.policies?.shiftType?.toUpperCase()} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Attendance Source" value={data.policies?.attendanceSource?.toUpperCase()} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Holiday Policy" value={data.policies?.holidayPolicy?.toUpperCase()} />
                    </Grid>
                </Grid>
            </SectionPaper>

            {/* --- 6. Documents & Other --- */}
            <SectionPaper title="6. Documents Uploaded">
                <List dense>
                    {data.documents && data.documents.length > 0 ? (
                        data.documents.map((doc, index) => (
                            <ListItem key={index} disableGutters>
                                <ListItemText
                                    primary={`${doc.type}:`}
                                    secondary={doc.name}
                                    sx={{ m: 0 }}
                                    primaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                                    secondaryTypographyProps={{ sx: { fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}
                                />
                            </ListItem>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>No documents uploaded.</Typography>
                    )}
                </List>
            </SectionPaper>

            {/* --- 7. Exit / Rehire Status --- */}
            <SectionPaper title="7. Exit / Rehire Status">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Current Status" value={data.status?.currentStatus} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Resignation Date" value={data.status?.resignationDate} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailItem label="Rehire Details" value={data.status?.rehireDetails} />
                    </Grid>
                </Grid>
            </SectionPaper>
        </Box>
    );
};

export default ViewAllDetailsModal;